import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Button, Checkbox, Tabs, TabsList, TabsTrigger, TimeGrid } from '@/components/ui';
import { buildTimeAxis, timePixel, type TimeScale, type TimeShift } from '@/lib/timeGrid';
import { DUTY_ROWS, SAMPLE_NOW, SHIFTS } from './model';

/** Shared local example; the gallery has read-only bars, workspace supplies its panel action. */
export function TimeGridExample({
  onShiftClick,
}: {
  onShiftClick?: (shift: TimeShift, trigger: HTMLElement) => void;
}) {
  const [scale, setScale] = useState<TimeScale>('week'),
    [periods, setPeriods] = useState(2);
  const [loading, setLoading] = useState(false),
    [error, setError] = useState<string>(),
    [fail, setFail] = useState(false);
  const viewport = useRef<HTMLDivElement>(null),
    timer = useRef<number | undefined>(undefined),
    busy = useRef(false);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const today = () => {
    const el = viewport.current;
    if (!el) return;
    const labelWidth = el.querySelector('[data-row-label]')?.getBoundingClientRect().width ?? 0;
    const axis = buildTimeAxis(scale, '2026-10-23', periods);
    el.scrollTo({
      left: Math.max(0, timePixel(SAMPLE_NOW, axis) - (el.clientWidth - labelWidth) / 2),
      behavior: 'instant',
    });
  };
  useLayoutEffect(() => {
    const el = viewport.current;
    if (!el) return;
    const labelWidth = el.querySelector('[data-row-label]')?.getBoundingClientRect().width ?? 0;
    const axis = buildTimeAxis(scale, '2026-10-23', scale === 'day' ? 1 : 2);
    el.scrollTo({
      left: Math.max(0, timePixel(SAMPLE_NOW, axis) - (el.clientWidth - labelWidth) / 2),
      top: 0,
      behavior: 'instant',
    });
  }, [scale]);
  const changeScale = (next: string) => {
    if (next === scale) return;
    window.clearTimeout(timer.current);
    busy.current = false;
    setLoading(false);
    setError(undefined);
    setScale(next as TimeScale);
    setPeriods(next === 'day' ? 1 : 2);
  };
  const load = () => {
    if (busy.current || periods >= 12) return;
    busy.current = true;
    setLoading(true);
    setError(undefined);
    timer.current = window.setTimeout(() => {
      if (fail) {
        setError('Не удалось добавить период. Показанные события сохранены.');
        setFail(false);
      } else setPeriods((n) => n + 1);
      setLoading(false);
      busy.current = false;
    }, 1100);
  };
  return (
    <div data-demo="schedule" className="flex min-w-0 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs variant="pill" value={scale} onValueChange={changeScale}>
          <TabsList aria-label="Масштаб времени">
            <TabsTrigger value="day">День</TabsTrigger>
            <TabsTrigger value="week">Неделя</TabsTrigger>
            <TabsTrigger value="month">Месяц</TabsTrigger>
          </TabsList>
        </Tabs>
        <Button variant="secondary" size="sm" onClick={today}>
          Сегодня
        </Button>
      </div>
      <TimeGrid
        rows={DUTY_ROWS}
        shifts={SHIFTS}
        scale={scale}
        anchor="2026-10-23"
        periods={periods}
        now={SAMPLE_NOW}
        viewportRef={viewport}
        loading={loading}
        error={error}
        hasMore={periods < 12}
        onLoadMore={load}
        onShiftClick={onShiftClick}
        label="График — вымышленные данные"
        rowHeading="Участник"
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-fg-muted">
          Пример на 23 октября 2026 года · Москва · UTC+3. Доступно до 12 периодов.
        </p>
        <Checkbox
          label="Сбой следующей подгрузки периода"
          checked={fail}
          onCheckedChange={(v) => setFail(v === true)}
        />
      </div>
    </div>
  );
}
